/**
 * £10KBURN — Pledge Tracker
 * assets/pledge-tracker.js
 *
 * Responsibilities:
 *  1. Fetch pledge data from Cloudflare Worker
 *  2. Drive landing animation (counter + padlock)
 *  3. Render pledge cards (Burn tab default)
 *  4. Tab switching — full section re-skin
 *  5. Sort + search
 *  6. FAQ two-level accordion
 */

(function () {
  'use strict';

  // ── Config ──────────────────────────────────────────────────────────────
  const WORKER_URL        = (window.PLEDGE_WORKER_URL || '').replace(/\/$/, '');
  const ANIMATION_SEEN_KEY = 'pledgeAnimationSeen';

  // ── Referral param — read once on load, appended to all Fillout + share URLs ─
  const REF_PARAM = new URLSearchParams(window.location.search).get('ref') || '';

  function filloutUrl(base) {
    return REF_PARAM ? base + '?ref=' + encodeURIComponent(REF_PARAM) : base;
  }

  // ── State ───────────────────────────────────────────────────────────────
  let pledgeData  = null;
  let activeTab   = 'burn';
  let sortKey     = 'date';
  let sortDirs    = { date: 'desc', amount: 'desc', name: 'asc' };
  let searchQuery = '';
  const PAGE_SIZE = 12;
  let visibleCount = PAGE_SIZE;

  // ── BURN-EDC state ───────────────────────────────────────────────────────
  let edcAnimFrameId     = null;
  let edcAnimTimers      = [];
  let edcScrambleInterval = null;
  let edcOpen            = false;
  let edcBadgeFrameEl    = null;

  // ── DOM helpers ─────────────────────────────────────────────────────────
  const $  = id  => document.getElementById(id);
  const $$ = sel => document.querySelectorAll(sel);

  // ── Number formatting ────────────────────────────────────────────────────
  function fmt(n) {
    return Math.round(n).toLocaleString('en-GB');
  }

  // ── Easing functions ─────────────────────────────────────────────────────
  function easeIn(t)    { return t * t * t; }
  function easeOut(t)   { return 1 - Math.pow(1 - t, 3); }
  function easeInOut(t) { return t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2, 3) / 2; }

  // ── Animated counter ─────────────────────────────────────────────────────
  function animateCounter(from, to, duration, easingFn, onUpdate, onDone) {
    const start = performance.now();
    function step(now) {
      const elapsed  = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const value    = from + (to - from) * easingFn(progress);
      onUpdate(value);
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        onDone && onDone();
      }
    }
    requestAnimationFrame(step);
  }

  function unlockArrow() {
    const svg  = document.getElementById('arrowCircleSvg');
    const ring = document.getElementById('arrowProgressRing');
    if (!svg || !ring) return;
    // Cancel the draw transition and snap to full — green ring is always complete
    ring.style.transition      = 'none';
    ring.style.strokeDashoffset = '0';
    svg.classList.add('arrow--unlocked');
  }

  // ── Landing animation sequence ────────────────────────────────────────────
  function runAnimation(burnTotal) {
    const counterEl = $('burnCounter');
    const padlockEl = $('burnPadlockWrap');

    // Trigger ring draw via CSS transition — red during counter (~3s),
    // switches green when padlock opens, completes ~6s total
    requestAnimationFrame(() => {
      const ring = document.getElementById('arrowProgressRing');
      if (ring) ring.style.strokeDashoffset = '0';
    });

    // If real total is below £9,900 race directly to it; otherwise race to £9,900 first
    const phase1Target   = Math.min(burnTotal, 9900);
    const phase1Duration = burnTotal < 9900 ? 1500 : 2000;

    // Phase 1 — dramatic race
    animateCounter(0, phase1Target, phase1Duration, easeOut,
      val => {
        counterEl.textContent = fmt(val);
      },
      () => {
        if (burnTotal <= 9900) {
          // No phase 2 needed — pop and finish
          popCounter(burnTotal, counterEl);
          revealChevron();
          return;
        }

        // Phase 2 — ease into real total; padlock turns green and shakes
        padlockEl.classList.add('padlock--approaching');

        animateCounter(9900, burnTotal, 1000, easeInOut,
          val => {
            counterEl.textContent = fmt(val);
          },
          () => {
            padlockEl.classList.remove('padlock--approaching');
            popCounter(burnTotal, counterEl);
            revealChevron();

            if (burnTotal >= 10000) {
              breakPadlockOpen(padlockEl);
            }
          }
        );
      }
    );
  }

  function popCounter(burnTotal, counterEl) {
    counterEl.textContent = fmt(burnTotal);
    const displayEl = $('burnCounterDisplay');
    if (!displayEl) return;
    displayEl.classList.add('counter--pop');
    // After pop finishes, swap to infinite pulse+glow
    setTimeout(() => {
      displayEl.classList.remove('counter--pop');
      displayEl.classList.add('counter--pulse');
    }, 550);
  }

  function revealChevron() {
    // Phase 2: arrow circle is always visible — nothing to reveal.
    // Click handler is wired in initArrow(); this function is kept for the
    // animation sequence call sites and the return-visitor fast-path.
  }

  function breakPadlockOpen(padlockEl) {
    padlockEl.classList.add('padlock--open');
    if (navigator.vibrate) navigator.vibrate([80, 40, 160]);
    spawnParticles(padlockEl);
    unlockArrow();
  }

  function spawnParticles(anchor) {
    const symbols = ['♥', '♥', '♥', '♥', '♥', '♥', '♥', '♥'];
    const colors  = ['#dc1e3d', '#006b3e', '#dc1e3d', '#006b3e', '#dc1e3d', '#006b3e'];
    const parent  = anchor.closest('.burn-landing') || anchor.parentElement;

    for (let i = 0; i < 90; i++) {
      const p = document.createElement('span');
      p.className   = 'burst-particle';
      p.textContent = symbols[i % symbols.length];

      // Straight radial flight — calculate X/Y directly, no character rotation
      const angle    = Math.random() * Math.PI * 2;
      const distance = 180 + Math.random() * 340;
      p.style.setProperty('--tx',       Math.cos(angle) * distance + 'px');
      p.style.setProperty('--ty',       Math.sin(angle) * distance + 'px');
      p.style.setProperty('--duration', (0.55 + Math.random() * 0.7) + 's');
      p.style.color = colors[Math.floor(Math.random() * colors.length)];
      parent.appendChild(p);
      setTimeout(() => p.remove(), 2000);
    }
  }

  // ── Totals display ────────────────────────────────────────────────────────
  function updateTotalsDisplay(totals) {
    const tabBurnEl   = $('tabBurnTotal');
    const tabGiveEl   = $('tabGiveTotal');
    const pendingDisp = $('burnPendingDisplay');
    const pendingAmt  = $('burnPendingAmount');

    if (tabBurnEl) tabBurnEl.textContent = fmt(totals.burnTotal);
    if (tabGiveEl) tabGiveEl.textContent = fmt(totals.giveTotal);

    // Burn tab pending subline — always visible, shows verified + pending combined
    const tabBurnPending    = $('tabBurnPending');
    const tabBurnPendingAmt = $('tabBurnPendingAmount');
    if (tabBurnPending && tabBurnPendingAmt) {
      tabBurnPendingAmt.textContent = fmt(totals.burnTotal + totals.burnPendingTotal);
    }

    // Give tab pending subline — always visible, verified + pending combined
    const givePendingTotal = (pledgeData && pledgeData.give)
      ? pledgeData.give.reduce((sum, r) => r.status === 'PENDING' ? sum + r.pledgeGBPValue : sum, 0)
      : 0;
    const tabGivePending    = $('tabGivePending');
    const tabGivePendingAmt = $('tabGivePendingAmount');
    if (tabGivePending && tabGivePendingAmt) {
      tabGivePendingAmt.textContent = fmt(totals.giveTotal + givePendingTotal);
    }
  }

  // ── Tab switching — entire section re-skins simultaneously ───────────────
  function switchTab(tab) {
    activeTab    = tab;
    searchQuery  = '';
    visibleCount = PAGE_SIZE;

    const searchEl    = $('burnSearch');
    const container   = $('burnDataContainer');
    const header      = $('burn2giveHeader');
    const tabBurnBtn  = $('tabBurn');
    const tabGiveBtn  = $('tabGive');
    const ctaBtn      = $('burnCtaBtn');

    if (searchEl)   searchEl.value     = '';
    if (container)  container.dataset.tab = tab;
    if (header)     header.dataset.tab    = tab;

    const isBurn = tab === 'burn';
    tabBurnBtn.classList.toggle('burn-tab--active', isBurn);
    tabGiveBtn.classList.toggle('burn-tab--active', !isBurn);

    if (ctaBtn) {
      if (isBurn) {
        ctaBtn.textContent = 'PLEDGE TO BURN';
        ctaBtn.href        = filloutUrl('https://churchofburn.fillout.com/pledge2burn');
      } else {
        ctaBtn.textContent = 'PLEDGE TO GIVE';
        ctaBtn.href        = filloutUrl('https://churchofburn.fillout.com/pledge2give');
      }
      ctaBtn.classList.toggle('burn-cta-btn--give', !isBurn);
    }

    renderCards();
  }

  // ── Card data helpers ─────────────────────────────────────────────────────
  function activeRecords() {
    if (!pledgeData) return [];
    return activeTab === 'burn' ? pledgeData.burn : pledgeData.give;
  }

  function sortedAndFiltered() {
    let records = activeRecords();

    // Filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      records = records.filter(r =>
        r.firstName.toLowerCase().includes(q)     ||
        r.lastName.toLowerCase().includes(q)      ||
        r.id.toLowerCase().includes(q)            ||
        r.pledgeCountry.toLowerCase().includes(q) ||
        (r.pledgeRegion  || '').toLowerCase().includes(q) ||
        (r.pledgePlace   || '').toLowerCase().includes(q)
      );
    }

    // Sort
    const dir = sortDirs[sortKey];
    records = [...records].sort((a, b) => {
      if (sortKey === 'date') {
        const va = new Date(a.pledgeDate).getTime();
        const vb = new Date(b.pledgeDate).getTime();
        return dir === 'asc' ? va - vb : vb - va;
      }
      if (sortKey === 'amount') {
        const va = a.pledgeGBPValue;
        const vb = b.pledgeGBPValue;
        return dir === 'asc' ? va - vb : vb - va;
      }
      // name
      const va = (a.firstName + ' ' + a.lastName).trim().toLowerCase();
      const vb = (b.firstName + ' ' + b.lastName).trim().toLowerCase();
      return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

    return records;
  }

  function fmtDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  }

  // ── Card rendering ────────────────────────────────────────────────────────
  function renderCards() {
    const cardsEl = $('burnCards');
    if (!cardsEl) return;

    const records = sortedAndFiltered();

    if (records.length === 0) {
      const empty = document.createElement('div');
      empty.className   = 'burn-empty';
      empty.textContent = searchQuery ? 'No pledges match your search.' : 'No pledges yet.';
      cardsEl.replaceChildren(empty);
      return;
    }

    const visible = records.slice(0, visibleCount);
    const hasMore = records.length > visibleCount;

    const frag = document.createDocumentFragment();
    for (const record of visible) {
      frag.appendChild(buildCard(record));
    }

    // Show More link
    if (hasMore) {
      const more = el('div', 'burn-show-more');
      const link = el('span', 'burn-show-more__link');
      link.textContent = 'Show more';
      const chevron = el('span', 'burn-show-more__chevron');
      chevron.textContent = '∨';
      more.appendChild(link);
      more.appendChild(chevron);
      more.addEventListener('click', () => {
        visibleCount += PAGE_SIZE;
        renderCards();
      });
      frag.appendChild(more);
    }

    cardsEl.replaceChildren(frag);
  }

  function buildCard(record) {
    const fullName = [record.firstName, record.lastName].filter(Boolean).join(' ');
    const nameLen  = fullName.length;
    const nameSizeCls = nameLen <= 20 ? 'pledge-name--large'
                      : nameLen <= 35 ? 'pledge-name--medium'
                      :                 'pledge-name--small';

    const card = el('div', 'pledge-card');
    if (record.hasAQ) {
      card.classList.add('burn-dc--pulsing');
      card.addEventListener('click', () => fetchAndOpenBurnEDC(record.airtableId));
    }

    // ── Body column (left) ───────────────────────────────────────────────
    const body = el('div', 'pledge-card__body');

    // Top band — Name + Pledge ID
    const top = el('div', 'pledge-card__top');

    const name = el('div', 'pledge-name ' + nameSizeCls);
    name.textContent = fullName || 'Anonymous'; // textContent — XSS safe

    const pledgeId = el('div', 'pledge-id');
    pledgeId.textContent = record.id;

    top.append(name, pledgeId);

    // Bottom band — Amount alongside meta (country / badge / date)
    const bottom = el('div', 'pledge-card__bottom');

    const amount = el('div', 'pledge-amount');
    amount.textContent = '£' + fmt(record.pledgeGBPValue);

    // Non-GBP: absolute overlay top-right of card, above the media square
    if (record.originalCurrencyCode && record.originalCurrencyCode !== 'GBP') {
      const origLine = el('div', 'pledge-orig-currency');
      origLine.appendChild(document.createTextNode('ORIGINAL PLEDGE'));
      origLine.appendChild(document.createElement('br'));
      origLine.appendChild(document.createTextNode(
        (record.currencySymbol || '') + fmt(record.originalAmount) + ' ' + record.originalCurrencyCode
      ));
      card.appendChild(origLine);
    }

    const meta = el('div', 'pledge-card__meta');

    const country = el('div', 'pledge-country');
    country.textContent = record.pledgeCountry || '';

    const badgeRow = el('div', 'pledge-badge-row');
    const badge    = el('span', 'pledge-badge pledge-badge--' + record.status.toLowerCase());
    badge.textContent = record.status;
    badgeRow.appendChild(badge);

    const date = el('div', 'pledge-date');
    date.textContent = fmtDate(record.pledgeDate);

    const dateRow = el('div', 'pledge-date-row');
    dateRow.append(date, country);
    const metaInner = el('div', 'pledge-card__meta-inner');
    metaInner.append(badgeRow, dateRow);
    meta.appendChild(metaInner);
    bottom.append(amount, meta);
    body.append(top, bottom);

    // ── Right column — media square ──────────────────────────────────────
    const right = el('div', 'pledge-card__right');
    const media = el('div', 'pledge-media');

    if (record.hasMedia && record.mediaURL) {
      const img = document.createElement('img');
      img.className = 'pledge-media__img';
      img.alt       = '';
      img.src       = record.mediaURL; // URL — not rendered as HTML
      media.appendChild(img);
    } else if (record.profileImageURL) {
      // Fall back to AQ profile image if no pledge media
      const img = document.createElement('img');
      img.className = 'pledge-media__img';
      img.alt       = '';
      img.src       = record.profileImageURL;
      media.appendChild(img);
    } else {
      const noMedia = el('div', 'pledge-media__none');
      noMedia.textContent = 'NO MEDIA';
      media.appendChild(noMedia);
    }

    right.appendChild(media);
    card.append(body, right);
    return card;
  }

  // Tiny element factory
  function el(tag, cls) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    return node;
  }

  // ── Sort controls ─────────────────────────────────────────────────────────
  function initSort() {
    $$('.sort-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.sort;
        if (sortKey === key) {
          sortDirs[key] = sortDirs[key] === 'asc' ? 'desc' : 'asc';
        } else {
          sortKey = key;
        }
        visibleCount = PAGE_SIZE;
        refreshSortUI();
        renderCards();
      });
    });
  }

  function refreshSortUI() {
    $$('.sort-btn').forEach(btn => {
      const key    = btn.dataset.sort;
      const active = key === sortKey;
      btn.classList.toggle('active', active);
      const arrow = btn.querySelector('.sort-arrow');
      if (arrow) arrow.textContent = sortDirs[key] === 'asc' ? '↑' : '↓';
    });
  }

  // ── Search ────────────────────────────────────────────────────────────────
  function initSearch() {
    const input = $('burnSearch');
    if (!input) return;
    input.addEventListener('input', () => {
      searchQuery  = input.value.trim();
      visibleCount = PAGE_SIZE;
      renderCards();
    });
  }

  // ── FAQ accordion ──────────────────────────────────────────────────────────
  function initFAQ() {
    const categories = $$('.faq-category');

    categories.forEach(cat => {
      const mainHeader = cat.querySelector('.faq-main-header');
      if (!mainHeader) return;

      mainHeader.addEventListener('click', () => {
        const wasOpen = cat.classList.contains('faq-category--open');
        // Close all categories
        categories.forEach(c => c.classList.remove('faq-category--open'));
        // Re-open if it was closed
        if (!wasOpen) cat.classList.add('faq-category--open');
      });

      // Section sub-headers (## level)
      cat.querySelectorAll('.faq-section').forEach(section => {
        const sectionHeader = section.querySelector('.faq-section-header');
        if (!sectionHeader) return;

        sectionHeader.addEventListener('click', e => {
          e.stopPropagation();
          const wasOpen = section.classList.contains('faq-section--open');
          // Close all sections in this category
          cat.querySelectorAll('.faq-section').forEach(s => s.classList.remove('faq-section--open'));
          if (!wasOpen) section.classList.add('faq-section--open');
        });
      });

      // Sub-items
      cat.querySelectorAll('.faq-sub-item').forEach(item => {
        const subHeader = item.querySelector('.faq-sub-header');
        if (!subHeader) return;

        subHeader.addEventListener('click', e => {
          e.stopPropagation();
          const wasOpen = item.classList.contains('faq-sub-item--open');
          // Close all sub-items in this category
          cat.querySelectorAll('.faq-sub-item').forEach(i => i.classList.remove('faq-sub-item--open'));
          if (!wasOpen) item.classList.add('faq-sub-item--open');
        });
      });
    });

    // Load with GENERAL open and first sub-question open
    const firstCat = categories[0];
    if (firstCat) {
      firstCat.classList.add('faq-category--open');
      const firstSub = firstCat.querySelector('.faq-sub-item');
      if (firstSub) firstSub.classList.add('faq-sub-item--open');
    }
  }

  // ── Scroll helpers ────────────────────────────────────────────────────────
  function initArrow() {
    const arrow = $('burnArrowDown');
    if (!arrow) return;
    arrow.addEventListener('click', () => {
      const target = $('burn-pledges');
      if (target) target.scrollIntoView({ behavior: 'smooth' });
    });
  }

  function initFaqLink() {
    const link = $('burnFaqLink');
    if (!link) return;
    link.addEventListener('click', e => {
      e.preventDefault();
      const target = $('burn-faq');
      if (target) target.scrollIntoView({ behavior: 'smooth' });
    });
  }

  function initFooterShare() {
    const btn = $('burnFooterShareBtn');
    if (!btn) return;
    const shareUrl = 'https://churchofburn.org/pages/10kburn' +
      (REF_PARAM ? '?ref=' + encodeURIComponent(REF_PARAM) : '');
    btn.addEventListener('click', function () {
      if (navigator.share) {
        navigator.share({ url: shareUrl }).catch(function () {});
      } else {
        navigator.clipboard.writeText(shareUrl).then(function () {
          // Reuse EDC toast pattern — create a simple fixed toast
          const toast = document.createElement('div');
          toast.className   = 'burn-edc-toast';
          toast.textContent = 'Link copied';
          document.body.appendChild(toast);
          setTimeout(function () { if (toast.parentNode) toast.remove(); }, 2100);
        }).catch(function () {});
      }
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // BURN-EDC — Burn Expanded Data Card
  // ══════════════════════════════════════════════════════════════════════════

  // ── Speedo arc SVG (needle excluded — needle is a separate CSS-animated div) ──
  const SPEEDO_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 500" width="500" height="500">' +
    '<defs><style>' +
    '.es0{fill:#dc1e3d}.es3{fill:sienna}.es4{fill:#d4af37}.es5{fill:#a8a8a8}.es6{fill:#cd7f32}' +
    '</style>' +
    '<filter id="ns" x="-50%" y="-50%" width="200%" height="200%">' +
    '<feDropShadow dx="0" dy="2" stdDeviation="4.8" flood-color="#000000" flood-opacity="0.35"/>' +
    '</filter>' +
    '</defs>' +
    '<path class="es5" d="M253.5,120.05c43.38,1.15,81.47,23.55,104.23,57.17l92.79-59.11C408.2,53.89,335.87,11.22,253.5,10.04v110Z"/>' +
    '<path class="es6" d="M142.27,177.22c22.76-33.62,60.85-56.02,104.23-57.17V10.04c-82.38,1.18-154.7,43.85-197.02,108.06l92.79,59.11Z"/>' +
    '<path class="es3" d="M138.51,183.12l-92.8-59.12c-22.64,36.62-35.71,79.78-35.71,126,0,36.26,8.06,70.63,22.45,101.45l99.71-46.5c-7.8-16.69-12.16-35.31-12.16-54.95,0-24.46,6.76-47.34,18.51-66.88Z"/>' +
    '<path class="es4" d="M454.29,124l-92.8,59.12c11.75,19.54,18.51,42.42,18.51,66.88,0,19.64-4.36,38.26-12.16,54.95l99.71,46.5c14.39-30.82,22.45-65.19,22.45-101.45,0-46.22-13.08-89.37-35.71-126Z"/>' +
    '<g>' +
    '<path class="es0" d="M184.89,308.75l13.95-31.61h7.31l-13.95,31.61h-7.31Z"/>' +
    '<path class="es0" d="M224.62,305.14c-2.94,0-5.42-.34-7.45-1.03-2.03-.68-3.65-1.6-4.85-2.76-1.21-1.16-2.08-2.45-2.62-3.88-.54-1.43-.81-2.87-.81-4.34,0-2.78.61-5.19,1.84-7.22,1.23-2.03,2.99-3.61,5.3-4.73,2.31-1.12,5.09-1.68,8.37-1.68,2.75,0,5.08.34,6.97,1.01,1.9.67,3.36,1.55,4.41,2.64,1.04,1.09,1.65,2.25,1.84,3.5h-7.03c-.3-1.04-.99-1.83-2.06-2.39-1.08-.55-2.47-.83-4.18-.83-1.6,0-2.99.31-4.18.92-1.19.61-2.11,1.53-2.76,2.76-.65,1.23-1.03,2.77-1.14,4.64v.21c.59-.64,1.38-1.2,2.34-1.7.97-.5,2.08-.88,3.35-1.17,1.26-.28,2.6-.42,4.02-.42,2.16,0,4.17.33,6.03.99,1.86.66,3.37,1.59,4.52,2.8,1.15,1.2,1.73,2.63,1.73,4.28,0,1.49-.57,2.87-1.7,4.16-1.13,1.29-2.72,2.31-4.77,3.08-2.05.77-4.43,1.15-7.14,1.15ZM224.17,301.03c1.34,0,2.52-.18,3.54-.53,1.02-.35,1.83-.84,2.43-1.47.59-.62.89-1.34.89-2.14,0-.85-.3-1.58-.89-2.19-.6-.61-1.41-1.1-2.45-1.45-1.04-.35-2.23-.53-3.57-.53s-2.47.18-3.51.53c-1.04.35-1.87.84-2.48,1.47-.61.63-.92,1.34-.92,2.14s.31,1.55.92,2.18c.61.63,1.45,1.12,2.51,1.47,1.06.35,2.24.53,3.54.53Z"/>' +
    '<path class="es0" d="M259.09,305.14c-2.68,0-5.1-.31-7.25-.92-2.16-.61-3.87-1.47-5.13-2.58-1.26-1.11-1.9-2.39-1.9-3.86,0-1.01.27-1.93.81-2.76.54-.83,1.29-1.56,2.26-2.19.97-.64,2.12-1.12,3.46-1.45-1.67-.5-3.02-1.2-4.04-2.12-1.02-.92-1.53-1.96-1.53-3.12s.5-2.24,1.51-3.26c1-1.01,2.49-1.83,4.46-2.44,1.97-.61,4.42-.92,7.36-.92s5.39.31,7.36.92c1.97.61,3.46,1.43,4.46,2.44,1,1.01,1.51,2.1,1.51,3.26s-.51,2.17-1.53,3.1c-1.02.93-2.37,1.65-4.04,2.14,1.34.35,2.5.84,3.49,1.47.98.62,1.75,1.35,2.29,2.18.54.83.81,1.75.81,2.76,0,1.46-.64,2.75-1.92,3.86-1.28,1.11-3,1.97-5.16,2.58-2.16.61-4.57.92-7.25.92ZM259.09,301.14c2.31,0,4.04-.35,5.19-1.06,1.15-.71,1.73-1.6,1.73-2.69,0-1.2-.6-2.15-1.81-2.85-1.21-.7-2.91-1.04-5.11-1.04s-3.86.35-5.1,1.04-1.87,1.65-1.87,2.85c0,1.09.59,1.98,1.79,2.69,1.19.71,2.92,1.06,5.19,1.06ZM259.09,289.99c1.9,0,3.36-.31,4.38-.92,1.02-.61,1.53-1.4,1.53-2.37,0-1.04-.51-1.85-1.53-2.42-1.02-.58-2.48-.87-4.38-.87s-3.31.29-4.35.87-1.56,1.4-1.56,2.46c0,.94.52,1.72,1.56,2.34,1.04.61,2.49.92,4.35.92Z"/>' +
    '<path class="es0" d="M296.42,305.14c-3.53,0-6.58-.54-9.15-1.63-2.57-1.09-4.53-2.59-5.89-4.51-1.36-1.92-2.04-4.15-2.04-6.67s.68-4.75,2.04-6.67c1.36-1.92,3.31-3.43,5.86-4.51,2.55-1.08,5.61-1.63,9.18-1.63s6.63.54,9.18,1.63c2.55,1.09,4.5,2.59,5.86,4.51,1.36,1.92,2.04,4.15,2.04,6.67s-.68,4.75-2.04,6.67c-1.36,1.92-3.31,3.43-5.86,4.51-2.55,1.08-5.61,1.63-9.18,1.63ZM296.42,300.86c1.86,0,3.5-.35,4.91-1.04s2.52-1.68,3.32-2.96,1.2-2.78,1.2-4.53-.4-3.29-1.2-4.55-1.91-2.24-3.32-2.92-3.05-1.03-4.91-1.03-3.5.34-4.91,1.03c-1.41.68-2.52,1.66-3.32,2.92-.8,1.26-1.2,2.78-1.2,4.55s.4,3.26,1.2,4.53c.8,1.27,1.91,2.26,3.32,2.96,1.41.7,3.05,1.04,4.91,1.04Z"/>' +
    '</g>' +
    '<rect class="burn-edc-needle-el" x="246.5" y="18" width="7" height="101" rx="3" fill="#dc1e3d" stroke="#f5f0e8" stroke-width="2" paint-order="stroke fill" filter="url(#ns)" transform="rotate(243, 250, 250)"/>' +
    '</svg>';

  // ── All 20 Designations for scramble animation ────────────────────────────
  const ALL_DESIGNATIONS = [
    'SPARK INITIATE', 'SMOULDERING INITIATE', 'KINDLED INITIATE', 'LIT INITIATE', 'FIERCE INITIATE',
    'BURNING ACOLYTE', 'ARDENT ACOLYTE', 'FERVENT ACOLYTE', 'AGITATOR ACOLYTE', 'ROARING ACOLYTE',
    'SPONTANEOUS IMMOLATOR', 'VOLATILE IMMOLATOR', 'INCENDIARY IMMOLATOR', 'MOLOTOV IMMOLATOR', 'TWISTED IMMOLATOR',
    'DEFIANT PROMETHEAN', 'SCORCHEDEARTH PROMETHEAN', 'UNBOUNDED PROMETHEAN', 'SUN-STEALING PROMETHEAN', 'TOTAL BURN PROMETHEAN',
  ];

  // ── EDC asset maps (populated from window.BURN_EDC_ASSETS set in Liquid) ──
  const _EDC = window.BURN_EDC_ASSETS || {};
  const BURN_EDC_BADGE = {
    Initiate:   _EDC.badgeInitiate,
    Acolyte:    _EDC.badgeAcolyte,
    Immolator:  _EDC.badgeImmolator,
    Promethean: _EDC.badgePromethean,
    _holder:    _EDC.badgeHolder,
  };

  // ── Holder badge SVG inlined — lets JS target #QuestionMark directly ────────
  const BADGE_HOLDER_SVG = `<svg id="burn-badge-holder-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 150">
  <defs><style>.bh-st0{isolation:isolate}.bh-st1{fill:#d4c9b8}.bh-st1,.bh-st2{stroke:#9e9590;stroke-miterlimit:10;stroke-width:.25px}.bh-st2{fill:#9e9590}.bh-st3{fill:#6b6460}</style></defs>
  <g id="BH_Inner">
    <g id="BH_Outer"><path class="bh-st1" d="M85.32,102.96l-.03-19.6c8.83-8.79,14.14-20.89,14.14-34.22C99.43,22.26,77.27.46,49.93.46S.43,22.26.43,49.14c0,13.33,5.45,25.41,14.28,34.2l-.08,19.62h11.57l-18.07,31.29,20.2-4.97,5.79,19.98,15.91-27.56,15.91,27.56,5.79-19.98,20.2,4.97-18.07-31.29s11.46,0,11.46,0Z"/></g>
    <path id="BH_UpperFlame" class="bh-st2" d="M91.71,66.49c3.76-6.9,5.27-21.18.61-33.34C82.65,7.85,47.24-7.6,19.28,16.77.72,32.93,4.64,57.89,6.53,61.65c-.38-9.47,5.69-14.63,9.82-18.38,1.27,6.88,5.08,6.76,9.48,11.5-2.4-29.77,27.37-39.6,28.91-41.34-5.63,13.01,5.16,19.61,11.34,28.6,1.02,1.48,4.61,7.52,3.83,9.13,2.15-4.51,4.63-10.26,9.94-11.68,2.24,10.55,12.99,16.41,11.86,27.01h0Z"/>
    <path id="BH_LowerFlame" class="bh-st2" d="M80.3,98H19.7l.05-10.01c5.09-.09,7.59-.11,11.2-.11-4.41-5.03-4.16-11.38,1.1-17.26,2.03-2.27,10.88-9.6,12.4-12.24,1.52-2.64,1.98-5.25.16-7.69,8.07,4.32,14.29,7.01,14.67,20.33,1.63-2.02,4-1.65,4.68-5.93,2.66,3.02,4.93,6.15,5.41,10.48.66,6.02-2.1,12.31-3.58,12.31h14.51s0,10.12,0,10.12Z"/>
    <polygon class="bh-st2" points="55 103 49.99 112.21 35.88 136.93 32.45 122.71 17.36 126.42 31.19 103.07 55 103"/>
    <polygon class="bh-st2" points="82.13 126.7 67.85 123.17 64.26 137 52.92 116.46 60 103 68.8 103 82.13 126.7"/>
  </g>
  <g id="QuestionMark" style="transform-box:fill-box;transform-origin:center center;transform:scale(0.5);">
    <g class="bh-st0"><g class="bh-st0">
      <path class="bh-st3" d="M35.72,81.88l-.6-19.32h5.76c3.04,0,5.88-.2,8.52-.6,2.64-.4,4.94-1.08,6.9-2.04,1.96-.96,3.5-2.34,4.62-4.14,1.12-1.8,1.68-4.14,1.68-7.02,0-2.48-.5-4.6-1.5-6.36-1-1.76-2.42-3.12-4.26-4.08-1.84-.96-4-1.44-6.48-1.44-2.64,0-4.88.5-6.72,1.5-1.84,1-3.28,2.36-4.32,4.08-1.04,1.72-1.56,3.78-1.56,6.18h-15.24c-.08-4.8,1-9.08,3.24-12.84,2.24-3.76,5.46-6.74,9.66-8.94,4.2-2.2,9.22-3.3,15.06-3.3,5.44,0,10.26.98,14.46,2.94,4.2,1.96,7.48,4.82,9.84,8.58,2.36,3.76,3.54,8.32,3.54,13.68s-1.18,10.08-3.54,13.68c-2.36,3.6-5.64,6.28-9.84,8.04-4.2,1.76-9.06,2.68-14.58,2.76l-.36,8.64h-14.28ZM42.92,109.6c-2.96,0-5.38-.9-7.26-2.7-1.88-1.8-2.82-4.02-2.82-6.66s.94-4.86,2.82-6.66c1.88-1.8,4.3-2.7,7.26-2.7s5.36.9,7.2,2.7c1.84,1.8,2.76,4.02,2.76,6.66s-.92,4.86-2.76,6.66c-1.84,1.8-4.24,2.7-7.2,2.7Z"/>
    </g></g>
  </g>
</svg>`;

  // ── score → needle angle (−90° at zero, +140° at 680) ────────────────────
  function scoreToAngle(score) {
    // Arc runs 243° → 117° clockwise through top (234° total sweep)
    return 243 + (Math.max(0, Math.min(680, score)) / 680) * 234;
  }

  // ── score → denomination tier name ───────────────────────────────────────
  function acsTierFromScore(score) {
    if (score < 170) return 'Initiate';
    if (score < 340) return 'Acolyte';
    if (score < 510) return 'Immolator';
    return 'Promethean';
  }

  // ── set needle CSS rotation + update zero-padded score display ───────────
  function setNeedleAndScore(needleEl, scoreEl, score) {
    const clamped = Math.max(0, Math.min(680, score));
    needleEl.setAttribute('transform', 'rotate(' + scoreToAngle(clamped) + ', 250, 250)');
    scoreEl.textContent = String(Math.round(clamped)).padStart(3, '0');
  }

  // Split at last space → two lines: e.g. "TOTAL BURN" + br + "PROMETHEAN".
  // white-space:nowrap on the element prevents CSS from re-wrapping either line.
  function setDesignationText(el, text) {
    var cut = text.lastIndexOf(' ');
    el.replaceChildren();
    if (cut === -1) {
      el.appendChild(document.createTextNode(text));
    } else {
      el.appendChild(document.createTextNode(text.slice(0, cut)));
      el.appendChild(document.createElement('br'));
      el.appendChild(document.createTextNode(text.slice(cut + 1)));
    }
  }

  // ── cancel any running ACS animation RAF + timeouts + scramble ───────────
  function cancelBurnEDCAnim() {
    if (edcAnimFrameId) { cancelAnimationFrame(edcAnimFrameId); edcAnimFrameId = null; }
    edcAnimTimers.forEach(clearTimeout);
    edcAnimTimers = [];
    if (edcScrambleInterval) { clearInterval(edcScrambleInterval); edcScrambleInterval = null; }
    if (edcBadgeFrameEl) {
      var _qm  = edcBadgeFrameEl.querySelector('#QuestionMark');
      if (_qm) { _qm.style.transition = 'none'; _qm.style.transform = 'scale(0.5)'; }
      var _svg = edcBadgeFrameEl.querySelector('svg');
      if (_svg) _svg.style.overflow = '';               // restore SVG default clipping
      edcBadgeFrameEl.style.overflow = '';              // restore frame CSS rule (overflow:hidden)
      var _zone = edcBadgeFrameEl.parentElement;
      if (_zone) _zone.style.overflow = '';             // restore zone CSS rule (overflow:hidden)
      edcBadgeFrameEl = null;
    }
  }

  // ── show "Link copied" toast ──────────────────────────────────────────────
  function showEDCToast() {
    const overlay = document.getElementById('burnEDCOverlay');
    if (!overlay) return;
    const existing = overlay.querySelector('.burn-edc-toast');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.className = 'burn-edc-toast';
    toast.textContent = 'Link copied';
    overlay.appendChild(toast);
    setTimeout(() => { if (toast.parentNode) toast.remove(); }, 2100);
  }

  // ── Badge zone explosion — 24 grey hearts/sparks burst from badge centre ──
  function spawnBadgeParticles(badgeFrameEl) {
    var rect   = badgeFrameEl.getBoundingClientRect();
    var ox     = rect.left + rect.width  * 0.5;
    var oy     = rect.top  + rect.height * 0.5;
    var syms   = ['♥', '♥', '✦', '♥', '✦', '♥'];

    for (var i = 0; i < 24; i++) {
      var p       = document.createElement('span');
      p.className = 'badge-burst-particle';
      // Evenly spread angles with slight randomness for natural look
      var angle    = (i / 24) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      var distance = 45 + Math.random() * 70; // 45–115px — contained burst
      p.style.setProperty('--tx',       (Math.cos(angle) * distance) + 'px');
      p.style.setProperty('--ty',       (Math.sin(angle) * distance) + 'px');
      p.style.setProperty('--duration', (0.4 + Math.random() * 0.4)  + 's');
      p.style.left    = ox + 'px';
      p.style.top     = oy + 'px';
      p.textContent   = syms[i % syms.length];
      document.body.appendChild(p);
      (function (el) {
        setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 1200);
      }(p));
    }
  }

  // ── 2-phase ACS needle + ? animation ────────────────────────────────────
  // Badge body stays fixed at SVG size throughout. Only #QuestionMark scales.
  function runACSAnimation(needleEl, scoreEl, badgeFrameEl, designationEl, trueScore, badgeTier) {
    cancelBurnEDCAnim();
    edcBadgeFrameEl = badgeFrameEl;

    var approach     = Math.max(0, trueScore - 50);
    var resolved     = false;
    var popFired     = false; // true once CSS pop starts — stops RAF from fighting the transition
    var holderSvgEl  = badgeFrameEl.querySelector('svg');
    var questionMark = holderSvgEl ? holderSvgEl.querySelector('#QuestionMark') : null;
    var badgeZoneEl  = badgeFrameEl.parentElement;

    // Toggle overflow on all three clipping layers: SVG viewport, badge frame, badge zone.
    // Called visible=true at pop moment, visible=false once badge has settled.
    function setOverflowClip(clipped) {
      var v = clipped ? '' : 'visible'; // '' reverts to CSS rule (overflow:hidden)
      badgeFrameEl.style.overflow = v;
      if (badgeZoneEl)  badgeZoneEl.style.overflow  = v;
      if (holderSvgEl)  holderSvgEl.style.overflow  = v;
    }

    // Scale constants
    var Q_START     = 0.5;    // 0s start
    var Q_POP_START = 1.35;   // reached at 4.0s, then CSS pop fires
    var Q_POP_PEAK  = 1.60;   // CSS pop target (4.0–4.3s)
    // At end of Phase 1 (3000ms = 75% of the 4000ms linear-growth window):
    var Q_P1_END    = Q_START + (Q_POP_START - Q_START) * (3 / 4); // = 1.1375

    function setQScale(s) {
      if (questionMark) questionMark.style.transform = 'scale(' + s + ')';
    }

    // Preload earned badge img — src set now so it's decoded well before the 4.9s swap
    var earnedImg        = document.createElement('img');
    earnedImg.className  = 'burn-edc-badge-img';
    earnedImg.alt        = '';
    earnedImg.src        = BURN_EDC_BADGE[badgeTier] || BURN_EDC_BADGE._holder || '';

    // Initial ? state
    setQScale(Q_START);

    // Designation scramble
    var scrambleIdx = 0;
    edcScrambleInterval = setInterval(function () {
      scrambleIdx = (scrambleIdx + Math.floor(Math.random() * 19) + 1) % ALL_DESIGNATIONS.length;
      setDesignationText(designationEl, ALL_DESIGNATIONS[scrambleIdx]);
    }, 35);

    // Resolve needle + designation — badge swap handled separately via overlap sequence
    function resolveAll() {
      if (resolved) return;
      resolved = true;
      clearInterval(edcScrambleInterval);
      edcScrambleInterval = null;
      setNeedleAndScore(needleEl, scoreEl, trueScore);
      setDesignationText(designationEl, designationEl.dataset.trueDesignation || '');
      designationEl.classList.remove('burn-edc-designation-pop');
      void designationEl.offsetWidth;
      designationEl.classList.add('burn-edc-designation-pop');
    }

    // Generic phase runner with optional per-frame tick
    function animPhase(fromScore, toScore, duration, easingFn, onTick, onDone) {
      var start = performance.now();
      function tick(now) {
        var t = Math.min((now - start) / duration, 1);
        var score = fromScore + (toScore - fromScore) * easingFn(t);
        setNeedleAndScore(needleEl, scoreEl, score);
        if (onTick) onTick(t);
        if (t < 1) {
          edcAnimFrameId = requestAnimationFrame(tick);
        } else {
          edcAnimFrameId = null;
          onDone();
        }
      }
      edcAnimFrameId = requestAnimationFrame(tick);
    }

    // ── ANIMATION SEQUENCE ────────────────────────────────────────────────
    // ? grows linearly 0.5→1.35 over 4000ms total.
    // Phase 1 (3000ms) covers the first 75%: 0.5→1.1375.
    // Phase 2 first 1000ms covers the remaining 25%: 1.1375→1.35.
    //
    // Phase 2 setTimeouts (all relative to Phase 2 start = 3000ms total):
    //  1000ms (4.0s) — ? CSS pop 1.35→1.60, ease-out 0.3s
    //  1300ms (4.3s) — explosion + grey badge fade-out (0.4s)
    //  1500ms (4.5s) — earned badge pops in 0.25→1.60, ease-out 0.2s
    //  1700ms (4.7s) — earned badge settles 1.60→1.0, ease-out 0.2s
    //  1900ms (4.9s) — cleanup + resolve needle/designation

    // Phase 1 — linear 3000ms, ? 0.5 → 1.1375
    animPhase(0, approach, 3000, function (t) { return t; }, function (t) {
      if (!popFired) setQScale(Q_START + t * (Q_P1_END - Q_START));
    }, function () {

      // Phase 2 — easeOut 2000ms (needle only after 1000ms; ? CSS-driven after pop)
      animPhase(approach, trueScore, 2000, easeOut, function (t) {
        if (!popFired && t <= 0.5) setQScale(Q_P1_END + (t / 0.5) * (Q_POP_START - Q_P1_END));
      }, function () {
        resolveAll(); // no-op if already resolved
      });

      // 4.0s — ? CSS pop: 1.35 → 1.60 (0.3s ease-out)
      edcAnimTimers.push(setTimeout(function () {
        popFired = true;
        setOverflowClip(false); // open all three overflow layers so ? can break container bounds
        if (questionMark) {
          // Snap to exact value first so CSS transition starts clean
          questionMark.style.transition = 'none';
          setQScale(Q_POP_START);
          requestAnimationFrame(function () {
            questionMark.style.transition = 'transform 0.3s ease-out';
            setQScale(Q_POP_PEAK);
          });
        }
      }, 1000));

      // 4.3s — popcorn explosion + grey badge fade (0.4s ease-in)
      edcAnimTimers.push(setTimeout(function () {
        spawnBadgeParticles(badgeFrameEl);
        if (holderSvgEl) {
          holderSvgEl.style.transition = 'opacity 0.4s ease-in';
          holderSvgEl.style.opacity    = '0';
        }
      }, 1300));

      // 4.5s — earned badge pops in + designation resolves simultaneously
      edcAnimTimers.push(setTimeout(function () {
        earnedImg.style.transform  = 'scale(0.25)';
        earnedImg.style.transition = 'none';
        badgeFrameEl.appendChild(earnedImg);
        resolveAll(); // stop scramble + fire designation pop at exact same moment as badge
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { // double-rAF ensures initial state paints first
            earnedImg.style.transition = 'transform 0.2s ease-out';
            earnedImg.style.transform  = 'scale(1.60)';
          });
        });
      }, 1500));

      // 4.7s — earned badge settles: scale(1.60) → scale(1.0), ease-out 0.2s
      edcAnimTimers.push(setTimeout(function () {
        earnedImg.style.transition = 'transform 0.2s ease-out';
        earnedImg.style.transform  = 'scale(1.0)';
      }, 1700));

      // 4.9s — cleanup + resolve needle/designation
      edcAnimTimers.push(setTimeout(function () {
        earnedImg.style.transition = 'none';
        // Remove faded holder SVG from DOM
        if (holderSvgEl && holderSvgEl.parentNode === badgeFrameEl) {
          badgeFrameEl.removeChild(holderSvgEl);
        }
        setOverflowClip(true); // restore all three overflow layers — badge fully settled
        resolveAll();
      }, 1900));
    });
  }

  // ── render acsStatement / personalStatement as paragraph elements ─────────
  function renderTextWithIndent(text, containerEl) {
    var paras = text.split(/\r?\n+/).filter(function (p) { return p.trim(); });
    paras.forEach(function (para) {
      var p = document.createElement('p');
      p.textContent = para.trim();
      containerEl.appendChild(p);
    });
  }

  // ── media lightbox ────────────────────────────────────────────────────────
  function openMediaLightbox(att) {
    var overlay = el('div', 'burn-edc-lightbox');
    var inner   = el('div', 'burn-edc-lightbox-inner');
    var closeBtn = el('button', 'burn-edc-lightbox-close');
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.textContent = '×';

    var isVideo = att.type && att.type.startsWith('video/');
    if (isVideo) {
      var vid = document.createElement('video');
      vid.className  = 'burn-edc-lightbox-media';
      vid.src        = att.url || '';
      vid.controls   = true;
      vid.autoplay   = true;
      vid.playsInline = true;
      inner.appendChild(vid);
    } else {
      var img = document.createElement('img');
      img.className = 'burn-edc-lightbox-media';
      img.src       = att.url || '';
      img.alt       = '';
      inner.appendChild(img);
    }

    inner.appendChild(closeBtn);
    overlay.appendChild(inner);
    document.body.appendChild(overlay);

    function closeLightbox() {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }
    closeBtn.addEventListener('click', closeLightbox);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) closeLightbox(); });
  }

  // ── build media zone (placeholder or real media) ──────────────────────────
  function buildMediaZone(mediaAttachments) {
    var zone     = el('div', 'burn-edc-media');
    var hasMedia = Array.isArray(mediaAttachments) && mediaAttachments.length > 0;

    if (hasMedia) {
      zone.classList.add('burn-edc-media--populated');
      mediaAttachments.slice(0, 4).forEach(function (att) {
        var isVideo = att.type && att.type.startsWith('video/');
        var wrap    = el('div', 'burn-edc-media-item-wrap' + (isVideo ? ' burn-edc-media-item-wrap--video' : ''));

        if (isVideo) {
          var vid = document.createElement('video');
          vid.className   = 'burn-edc-media-item';
          vid.src         = att.url || '';
          vid.muted       = true;
          vid.playsInline = true;
          vid.preload     = 'metadata';
          wrap.appendChild(vid);
        } else {
          var img = document.createElement('img');
          img.className = 'burn-edc-media-item';
          img.src       = att.url || '';
          img.alt       = '';
          wrap.appendChild(img);
        }

        wrap.addEventListener('click', function () { openMediaLightbox(att); });
        zone.appendChild(wrap);
      });
    } else {
      var placeholders = [
        { cls: 'burn-edc-media-placeholder burn-edc-media-p1', text: 'UPLOAD A PHOTO OF THE NOTE(S) YOU\'LL BURN?' },
        { cls: 'burn-edc-media-placeholder burn-edc-media-p2', text: 'UPLOAD A PHOTO OF YOUR HARDCOPY PLEDGE CERTIFICATE?' },
        { cls: 'burn-edc-media-placeholder burn-edc-media-p3', text: 'UPLOAD YOUR BURN PHOTOS?' },
      ];
      placeholders.forEach(function (ph) {
        var box = el('div', ph.cls);
        var txt = el('div', 'burn-edc-media-placeholder-text');
        txt.textContent = ph.text;
        box.appendChild(txt);
        zone.appendChild(box);
      });
    }
    return zone;
  }

  // ── build share button + disclaimer + report link ─────────────────────────
  function buildBurnEDCFooter(pledgeId, airtableId) {
    var frag = document.createDocumentFragment();

    // Share button
    var shareWrap = el('div', 'burn-edc-share');
    var shareBtn  = el('button', 'burn-edc-share-btn');
    shareBtn.type = 'button';
    shareBtn.setAttribute('aria-label', 'Share');
    // Material Symbols share icon (Android convention)
    shareBtn.innerHTML = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>' +
      '</svg>';
    shareBtn.addEventListener('click', function () {
      var shareUrl = 'https://churchofburn.org/pages/10kburn?pledge=' + encodeURIComponent(airtableId || pledgeId) + '&ref=' + encodeURIComponent(pledgeId);
      if (navigator.share) {
        navigator.share({ url: shareUrl }).catch(function () {});
      } else {
        navigator.clipboard.writeText(shareUrl).then(function () {
          showEDCToast();
        }).catch(function () {});
      }
    });
    shareWrap.appendChild(shareBtn);
    frag.appendChild(shareWrap);

    // Disclaimer
    var disclaimer = el('p', 'burn-edc-disclaimer');
    disclaimer.textContent = 'Anti-Credit Score-O-Meter® is a proprietary assessment tool of Church of Burn’s Anti-Credit Bureau. No financial institutions were harmed in the production of this score. © Church of Burn 2026. All rights reserved. Your score may go up as well as down.';
    frag.appendChild(disclaimer);

    // Report link
    var report = document.createElement('a');
    report.className = 'burn-edc-report-link';
    report.textContent = 'REPORT THIS CONTENT';
    report.href = 'mailto:info@churchofburn.org?subject=REPORT%20THIS%20CONTENT&body=Pledge%20ID%3A%20' + encodeURIComponent(pledgeId);
    frag.appendChild(report);

    return frag;
  }

  // ── build all content inside #burnEDCCard ─────────────────────────────────
  function buildBurnEDCContent(data) {
    var cardEl  = document.getElementById('burnEDCCard');
    if (!cardEl) return;
    cardEl.replaceChildren();

    var aq        = data.aq || {};
    var firstName = data.anonymous ? 'Anonymous' : (data.firstName || '');
    var lastName  = data.anonymous ? '' : (data.lastName || '');
    var fullName  = [firstName, lastName].filter(Boolean).join(' ');
    var trueScore = aq.antiCreditScore || 0;
    var badgeTier = aq.denomination || 'Initiate';

    // ── Two-column area ───────────────────────────────────────────────────
    var cols    = el('div', 'burn-edc-header-cols');
    var colLeft = el('div', 'burn-edc-col-left');
    var colRight = el('div', 'burn-edc-col-right');

    // Left — pledge ID, name, amount (above speedo)
    var pledgeNum = el('div', 'burn-edc-pledge-number');
    pledgeNum.textContent = data.id;
    colLeft.appendChild(pledgeNum);

    var nameEl = el('div', 'burn-edc-name');
    nameEl.textContent = fullName || 'Anonymous';
    colLeft.appendChild(nameEl);

    var amountEl = el('div', 'burn-edc-amount');
    var currencyEl = el('span', 'burn-edc-currency');
    currencyEl.textContent = '£';
    var valueEl = el('span', 'burn-edc-value');
    valueEl.textContent = fmt(data.pledgeGBPValue || 0);
    amountEl.append(currencyEl, valueEl);
    colLeft.appendChild(amountEl);

    // Left — speedo
    var speedoWrap = el('div', 'burn-edc-speedo-wrap');
    var speedoSvgEl = el('div', 'burn-edc-speedo-svg');
    speedoSvgEl.innerHTML = SPEEDO_SVG; // safe — own SVG markup, not user data
    var needleEl = speedoSvgEl.querySelector('.burn-edc-needle-el'); // SVG rect, pivots at (250,250)
    var scoreDisplay = el('div', 'burn-edc-score-display');
    var scoreEl = el('span', 'burn-edc-score-number');
    scoreEl.textContent = '000';
    scoreDisplay.appendChild(scoreEl);
    speedoWrap.append(speedoSvgEl, scoreDisplay);
    colLeft.appendChild(speedoWrap);

    // Left — cta lines (pinned to bottom of colLeft, aligns with plaque bottom)
    var ctaLines = el('div', 'burn-edc-cta-lines');
    ctaLines.appendChild(document.createTextNode('ARE YOU A SIMP FOR CAPITAL?'));
    ctaLines.appendChild(document.createElement('br'));
    ctaLines.appendChild(document.createTextNode('IS YOUR ACS A BIG FAT ZERO?'));
    colLeft.appendChild(ctaLines);

    // Right — profile image
    var profileEl = el('div', 'burn-edc-profile');
    if (aq.profileImageURL) {
      var profileImg = document.createElement('img');
      profileImg.alt = '';
      profileImg.src = aq.profileImageURL;
      profileEl.appendChild(profileImg);
    } else {
      var profilePlaceholder = el('div', 'burn-edc-profile-placeholder');
      profilePlaceholder.textContent = 'ADD A PROFILE PICTURE';
      profileEl.appendChild(profilePlaceholder);
    }
    colRight.appendChild(profileEl);

    // Right — badge zone + designation
    // badgeZone holds only the badge frame (SVG + earned img stacked)
    // designationEl is a colRight sibling with margin-top:auto so its bottom
    // aligns with colLeft bottom (CTA lines) via flexbox stretch
    var badgeZone  = el('div', 'burn-edc-badge-zone');
    var badgeFrame = el('div', 'burn-edc-badge-frame');
    badgeFrame.innerHTML = BADGE_HOLDER_SVG; // inlined — JS can target #QuestionMark directly

    var designationEl = el('div', 'burn-edc-designation-text');
    designationEl.dataset.trueDesignation = aq.designationFinal || '';
    setDesignationText(designationEl, ALL_DESIGNATIONS[0]);

    badgeZone.appendChild(badgeFrame);
    colRight.appendChild(badgeZone);
    colRight.appendChild(designationEl); // after badgeZone — margin-top:auto pushes to bottom

    cols.append(colLeft, colRight);
    cardEl.appendChild(cols);

    // ── CTA zone (link only — lines moved into colLeft) ───────────────────
    var ctaEl    = el('div', 'burn-edc-cta');
    var ctaLink = document.createElement('a');
    ctaLink.className = 'burn-edc-cta-link';
    ctaLink.href = filloutUrl('https://churchofburn.fillout.com/pledge2burn');
    ctaLink.appendChild(document.createTextNode('Pledge Now and get '));
    var ctaEm = document.createElement('em');
    ctaEm.textContent = 'YOUR';
    ctaLink.appendChild(ctaEm);
    ctaLink.appendChild(document.createTextNode(' Anti-Credit Score'));
    ctaEl.append(ctaLink);
    cardEl.appendChild(ctaEl);

    // ── ACS Statement ─────────────────────────────────────────────────────
    var acsHeader = el('div', 'burn-edc-section-header');
    acsHeader.textContent = 'Bureau Statement';
    var acsWrap = el('div', 'burn-edc-body-wrap');
    var acsBody = el('div', 'burn-edc-section-body burn-edc-acs-body');
    renderTextWithIndent(aq.acsStatement || '', acsBody);
    acsWrap.appendChild(acsBody);
    cardEl.append(acsHeader, acsWrap);

    // ── Personal Statement (conditional) ──────────────────────────────────
    var hasPS = !!(aq.personalStatement && aq.personalStatement.trim());
    var psBody = null;
    if (hasPS) {
      var psHeaderWrap = el('div', 'burn-edc-ps-header-wrap');
      var psHeader = el('div', 'burn-edc-section-header');
      psHeader.textContent = 'Personal Statement';
      psHeaderWrap.appendChild(psHeader);
      var psWrap = el('div', 'burn-edc-body-wrap');
      psBody = el('div', 'burn-edc-section-body');
      renderTextWithIndent(aq.personalStatement, psBody);
      psWrap.appendChild(psBody);
      cardEl.append(psHeaderWrap, psWrap);
    }

    // ── Media zone (uses AQ media uploads, falls back to empty) ──────────
    cardEl.appendChild(buildMediaZone(aq ? aq.mediaAttachments : []));

    // ── Footer ────────────────────────────────────────────────────────────
    cardEl.appendChild(buildBurnEDCFooter(data.id, data.airtableId));

    // Set conditional body heights after layout — must be post-render
    requestAnimationFrame(function () {
      var w         = cardEl.clientWidth * (1 - 0.064); // 3.2% left + 3.2% right padding
      var soloBodyH = Math.min(w * 0.83, 830);

      if (!hasPS) {
        acsBody.style.height = soloBodyH + 'px';
      } else {
        // Measure actual rendered header/gap sizes so maths is viewport-accurate
        var acsHeaderH = acsHeader.offsetHeight;
        var acsWrapMT  = parseFloat(getComputedStyle(acsWrap).marginTop)      || 0;
        var psHWrapMT  = parseFloat(getComputedStyle(psHeaderWrap).marginTop) || 0;
        var psHWrapH   = psHeaderWrap.offsetHeight;
        var psWrapMT   = parseFloat(getComputedStyle(psWrap).marginTop)       || 0;

        // Total height the ACS section occupies when displayed alone
        var totalH = acsHeaderH + acsWrapMT + soloBodyH;

        // Split 65 / 35 — BS section gets 65%, PS section gets 35%
        var bsH = Math.round(totalH * 0.65);
        var psH = totalH - bsH;

        acsBody.style.height = Math.max(40, bsH - acsHeaderH - acsWrapMT)                     + 'px';
        psBody.style.height  = Math.max(40, (psH - psHWrapMT - psHWrapH - psWrapMT) * 1.1)  + 'px';
      }

      // Kick off ACS animation
      runACSAnimation(needleEl, scoreEl, badgeFrame, designationEl, trueScore, badgeTier);
    });
  }

  // ── open BURN-EDC with fetched data ───────────────────────────────────────
  function openBurnEDC(data) {
    edcOpen = true;
    var overlay = document.getElementById('burnEDCOverlay');
    if (!overlay) return;
    overlay.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    buildBurnEDCContent(data);
  }

  // ── close BURN-EDC ────────────────────────────────────────────────────────
  function closeBurnEDC() {
    edcOpen = false;
    cancelBurnEDCAnim();
    var overlay = document.getElementById('burnEDCOverlay');
    if (overlay) overlay.setAttribute('hidden', '');
    document.body.style.overflow = '';
    var cardEl = document.getElementById('burnEDCCard');
    if (cardEl) cardEl.replaceChildren();
  }

  // ── fetch single BURN record and open EDC ────────────────────────────────
  function fetchAndOpenBurnEDC(pledgeId) {
    fetch(WORKER_URL + '/pledges/burn/' + encodeURIComponent(pledgeId))
      .then(function (res) {
        if (!res.ok) throw new Error('Worker responded ' + res.status);
        return res.json();
      })
      .then(function (data) {
        openBurnEDC(data);
      })
      .catch(function (err) {
        console.error('BURN-EDC fetch failed for ' + pledgeId + ':', err);
      });
  }

  // ── init BURN-EDC (close button + ESC + backdrop click) ──────────────────
  function initBurnEDC() {
    var overlay = document.getElementById('burnEDCOverlay');
    if (overlay) {
      // Move to body to escape any Shopify transform/stacking-context containers
      // that would prevent position:fixed from covering the full viewport.
      document.body.appendChild(overlay);

      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) closeBurnEDC();
      });
    }

    var closeBtn = document.getElementById('burnEDCClose');
    if (closeBtn) closeBtn.addEventListener('click', closeBurnEDC);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && edcOpen) closeBurnEDC();
    });
  }

  // ── Tabs ──────────────────────────────────────────────────────────────────
  function initTabs() {
    const burnTab = $('tabBurn');
    const giveTab = $('tabGive');
    if (burnTab) burnTab.addEventListener('click', () => switchTab('burn'));
    if (giveTab) giveTab.addEventListener('click', () => switchTab('give'));
  }

  // ── Main init ─────────────────────────────────────────────────────────────
  async function init() {
    initArrow();
    initFaqLink();
    initFooterShare();
    initTabs();
    initSort();
    initSearch();
    initFAQ();
    initBurnEDC();

    // Route 2 — open BURN-EDC immediately if ?pledge= param present
    const pledgeParam = new URLSearchParams(window.location.search).get('pledge');
    if (pledgeParam) fetchAndOpenBurnEDC(pledgeParam);

    const animationSeen = localStorage.getItem(ANIMATION_SEEN_KEY) === 'true';
    // Suppress landing animation for ?pledge= visitors — EDC is their entry point
    if (pledgeParam) {
      localStorage.setItem(ANIMATION_SEEN_KEY, 'true');
    }

    // Start animation immediately — no need to wait for fetch
    runAnimation(10000);
    localStorage.setItem(ANIMATION_SEEN_KEY, 'true');

    try {
      const res = await fetch(WORKER_URL + '/pledges');
      if (!res.ok) throw new Error('Worker responded ' + res.status);
      pledgeData = await res.json();

      updateTotalsDisplay(pledgeData.totals);
      renderCards();

      // Return visits: chevron visible immediately, don't make them wait for animation end
      if (animationSeen) revealChevron();

    } catch (err) {
      console.error('£10KBURN pledge fetch failed:', err);
      // Graceful degradation — show zero state, page still usable
      const counterEl = $('burnCounter');
      if (counterEl) counterEl.textContent = '0';
      revealChevron();

      const cardsEl = $('burnCards');
      if (cardsEl) {
        const msg = el('div', 'burn-empty');
        msg.textContent = 'Pledge data unavailable. Please try again shortly.';
        cardsEl.replaceChildren(msg);
      }
    }
  }

  document.addEventListener('DOMContentLoaded', init);

})();
